/**
 * Available Status Utilities
 *
 * Business Rules: "Pending" (Triaged/Detected) status visibility based on user type and mode.
 *
 * - Praetorian users (isPraetorianUser = true): See ALL statuses including Triaged
 * - Customer users (isPraetorianUser = false) in legacy mode: Triaged is hidden from UI
 * - Customer users (isPraetorianUser = false) in new mode: See ALL statuses (Detected visible)
 *
 * Rationale: Praetorian users need Triaged for internal workflows. In legacy mode,
 * customers get simplified workflow without intermediate triage state. In new mode
 * (new vulnerability system enabled), customers see "Detected" status to filter by.
 *
 * Implementation Date: 2025-11-09
 * Ticket: bugfix/do-not-show-pending-to-engagemetns-customers
 * Updated: 2025-12-15 - Added mode parameter for new vulnerability system
 * Ticket: bugfix/detecting-not-shown-to-customers
 *
 * Note: Backend still supports Triaged status for data migration and API compatibility.
 */

import { RiskStatus } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import {
  getStatusLabel,
  VulnerabilityStatusMode,
} from '@/utils/vulnerabilityStatus.util';

/**
 * Type representing risk statuses available in the UI
 * Excludes Triaged (hidden from customers in legacy mode) and Deleted (hidden from all users)
 *
 * Note: Praetorian users can see all RiskStatus values including Triaged
 */
export type AvailableRiskStatus = Exclude<
  RiskStatus,
  RiskStatus.Triaged | RiskStatus.Deleted
>;

/**
 * Status values available in the UI (excludes Triaged and Deleted)
 */
export const AVAILABLE_STATUSES = [
  RiskStatus.Opened,
  RiskStatus.Remediated,
  RiskStatus.Accepted,
] as const;

/**
 * Get list of status values available in UI
 * Filters out Triaged from all RiskStatus enum values based on user type and mode
 *
 * @param isPraetorianUser - Whether user is a Praetorian user (default: false)
 * @param mode - Vulnerability status display mode (default: 'legacy')
 * @returns Array of RiskStatus values (excludes Triaged for customers in legacy mode)
 *
 * @example
 * ```typescript
 * // For customer users in legacy mode
 * const customerStatuses = getAvailableStatuses(false, 'legacy');
 * // Returns: [Opened, Remediated, Accepted, Deleted]
 *
 * // For customer users with new vulnerability system enabled
 * const customerNewMode = getAvailableStatuses(false, 'new');
 * // Returns: [Triaged, Opened, Remediated, Accepted, Deleted] (Triaged labeled as "Detected")
 *
 * // For Praetorian users (any mode)
 * const praetorianStatuses = getAvailableStatuses(true);
 * // Returns: [Triaged, Opened, Remediated, Accepted, Deleted]
 * ```
 */
export function getAvailableStatuses(
  isPraetorianUser = false,
  mode: VulnerabilityStatusMode = 'legacy'
): RiskStatus[] {
  // All users (including Praetorian) never see Deleted in the UI
  if (mode === 'new') {
    // New mode: all statuses except Deleted
    return Object.values(RiskStatus).filter(
      status => status !== RiskStatus.Deleted
    );
  }
  // Legacy mode: exclude Triaged and Deleted
  // Praetorian users see Triaged but not Deleted (same as new mode customers minus Triaged)
  if (isPraetorianUser) {
    return Object.values(RiskStatus).filter(
      status => status !== RiskStatus.Deleted
    );
  }
  // Legacy mode customers don't see Triaged or Deleted
  return Object.values(RiskStatus).filter(
    status => status !== RiskStatus.Triaged && status !== RiskStatus.Deleted
  );
}

/**
 * Get dropdown options for available statuses
 * Returns options formatted for FilterDropdown and select components
 *
 * @param isPraetorianUser - Whether user is a Praetorian user (default: false)
 * @param mode - Vulnerability status display mode (default: 'legacy')
 * @returns Array of dropdown options with value, label, and id
 *
 * @example
 * ```typescript
 * const { isPraetorianUser } = useAuth();
 * const { mode } = useVulnerabilityStatusMode();
 * const options = getAvailableStatusOptions(isPraetorianUser, mode);
 * <FilterDropdown options={options} />
 * ```
 */
export function getAvailableStatusOptions(
  isPraetorianUser = false,
  mode: VulnerabilityStatusMode = 'legacy'
): DropdownOption[] {
  return getAvailableStatuses(isPraetorianUser, mode).map(status => ({
    value: status,
    label: getStatusLabel(status, mode),
    id: status,
  }));
}

/**
 * Get export options for available statuses
 * Returns options formatted for ExportVulnerabilities component
 *
 * @param isPraetorianUser - Whether user is a Praetorian user (default: false)
 * @param mode - Vulnerability status display mode (default: 'legacy')
 * @returns Array of export options with label and value
 *
 * @example
 * ```typescript
 * const { isPraetorianUser } = useAuth();
 * const { mode } = useVulnerabilityStatusMode();
 * const options = getAvailableStatusExportOptions(isPraetorianUser, mode);
 * <ExportVulnerabilities statusOptions={options} />
 * ```
 */
export function getAvailableStatusExportOptions(
  isPraetorianUser = false,
  mode: VulnerabilityStatusMode = 'legacy'
): Array<{
  label: string;
  value: RiskStatus;
}> {
  return getAvailableStatuses(isPraetorianUser, mode).map(status => ({
    label: getStatusLabel(status, mode),
    value: status,
  }));
}

/**
 * Type guard to check if a value is a valid RiskStatus enum value
 *
 * Use this for runtime validation of unknown status values from APIs,
 * URL parameters, or user input.
 *
 * @param value - Unknown value to validate
 * @returns true if value is a valid RiskStatus, with type narrowing
 *
 * @example
 * ```typescript
 * const statusFromUrl = searchParams.get('status');
 *
 * if (isValidRiskStatus(statusFromUrl)) {
 *   // TypeScript now knows statusFromUrl is RiskStatus
 *   updateFilter(statusFromUrl);
 * } else {
 *   console.warn('Invalid status from URL:', statusFromUrl);
 * }
 * ```
 */
export function isValidRiskStatus(value: unknown): value is RiskStatus {
  return (
    typeof value === 'string' &&
    Object.values(RiskStatus).includes(value as RiskStatus)
  );
}

/**
 * Check if a status is available for the current user type and mode
 *
 * NOTE: This is a boolean function, not a type guard, because the return value
 * depends on runtime user type and mode which TypeScript cannot narrow.
 *
 * @param status - RiskStatus to check
 * @param isPraetorianUser - Whether user is a Praetorian user (default: false)
 * @param mode - Vulnerability status display mode (default: 'legacy')
 * @returns true if status is available for the user type and mode
 *
 * @example
 * ```typescript
 * const { isPraetorianUser } = useAuth();
 * const { mode } = useVulnerabilityStatusMode();
 *
 * if (isStatusAvailable(risk.status, isPraetorianUser, mode)) {
 *   // Status can be displayed/selected
 * }
 *
 * // Combining with type guard for unknown values
 * const unknownStatus = getStatusFromApi();
 * if (isValidRiskStatus(unknownStatus) && isStatusAvailable(unknownStatus, isPraetorianUser, mode)) {
 *   // Safe to use: valid status AND available to user
 * }
 * ```
 */
export function isStatusAvailable(
  status: RiskStatus,
  isPraetorianUser = false,
  mode: VulnerabilityStatusMode = 'legacy'
): boolean {
  // Deleted is hidden for ALL users in the UI
  if (status === RiskStatus.Deleted) return false;
  return isPraetorianUser || mode === 'new' || status !== RiskStatus.Triaged;
}
